import { useState, useCallback } from 'react';

export function useReportGeneration({ authUser, getAllData, acquireDriveToken, DriveAPI }) {
    const [fiReportStatus, setFiReportStatus] = useState('idle'); // idle | uploading | done | error
    const [advisorReportStatus, setAdvisorReportStatus] = useState('idle'); // idle | uploading | done | error

    // ── FI Report: generate sanitized export ─────────────────
    // Strips ALL personally identifiable information before export.
    // No names, no account/card numbers, no bank references.
    const generateFIReport = useCallback(() => {
        const data = getAllData();
        const fmt2 = n => Math.round((n || 0) * 100) / 100;

        const goldPrice = typeof data.sgdRate === 'number' ? data.sgdRate : parseFloat(data.sgdRate) || 186;
        const lkrPerSgd = typeof data.lkrRate === 'number' ? data.lkrRate : parseFloat(data.lkrRate) || 245;
        const sgdPerUsd = typeof data.usdRate === 'number' ? data.usdRate : parseFloat(data.usdRate) || 1.26;
        const _base = data.baseCurrency || 'SGD';

        const toBaseLocal = (amount, fromCur) => {
            const n = parseFloat(amount) || 0;
            let inSGD;
            if (fromCur === 'SGD') inSGD = n;
            else if (fromCur === 'LKR') inSGD = n / lkrPerSgd;
            else if (fromCur === 'USD') inSGD = n * sgdPerUsd;
            else inSGD = n;
            if (_base === 'SGD') return inSGD;
            if (_base === 'LKR') return inSGD * lkrPerSgd;
            if (_base === 'USD') return inSGD / sgdPerUsd;
            return inSGD;
        };

        const goldByType = {};
        (data.goldInventory || []).forEach(item => {
            const key = `${item.type} (${item.quality})`;
            if (!goldByType[key]) goldByType[key] = { type: item.type, quality: item.quality, count: 0, totalWeightG: 0, totalPawn: 0, totalValueBase: 0 };
            const weight = parseFloat(item.weight) || 0;
            const qualityFactor = item.quality === '18K' ? 0.75 : 1.0;
            const marketValueSGD = weight * goldPrice * qualityFactor;
            goldByType[key].count += 1;
            goldByType[key].totalWeightG = fmt2(goldByType[key].totalWeightG + weight);
            goldByType[key].totalPawn = fmt2(goldByType[key].totalPawn + (item.pawn || 0));
            goldByType[key].totalValueBase = fmt2(goldByType[key].totalValueBase + toBaseLocal(marketValueSGD, 'SGD'));
        });

        const sanitizedPersonalLoans = (data.personalLoans || []).map((loan, idx) => ({
            loanRef: `LOAN-${String(idx + 1).padStart(2, '0')}`,
            type: loan.type || 'Personal Loan',
            currency: loan.currency || 'SGD',
            totalAmount: fmt2(loan.totalAmount),
            principalPaid: fmt2(loan.principalPaid),
            principalRemaining: fmt2(loan.principalRemaining),
            monthsLeft: loan.monthsLeft,
            monthlyInstallment: fmt2(loan.monthlyInstallment),
            settlementDate: loan.settlementDate || '',
            paymentHistory: (loan.paymentHistory || []).map(p => ({
                period: p.period, date: p.date, amountPaid: fmt2(p.amount), type: p.type,
            })),
        }));

        const sanitizedGoldLoans = (data.goldLoans || []).map((loan, idx) => ({
            loanRef: `GOLD-LOAN-${String(idx + 1).padStart(2, '0')}`,
            currency: loan.currency || 'LKR',
            goldWeightG: fmt2(loan.goldWeight),
            totalAmountOriginal: fmt2(loan.totalAmount),
            principalRemaining: fmt2(loan.principalRemaining),
            unpaidInterest: fmt2(loan.unpaidInterest),
            monthlyInterestRate: loan.monthlyRate,
            monthlyInterestAmount: fmt2(loan.monthlyInterest),
            paymentHistory: (loan.paymentHistory || []).map(p => ({
                period: p.period, date: p.date, amountPaid: fmt2(p.amount), type: p.type,
            })),
        }));

        const accountAgg = {};
        (data.accounts || []).forEach(acc => {
            const key = `${acc.country}-${acc.currency}`;
            if (!accountAgg[key]) accountAgg[key] = { country: acc.country, currency: acc.currency, accountCount: 0, totalNative: 0 };
            accountAgg[key].accountCount += 1;
            accountAgg[key].totalNative = fmt2(accountAgg[key].totalNative + (parseFloat(acc[`balance${acc.currency}`]) || 0));
        });

        const ccAgg = {};
        (data.creditCards || []).forEach(cc => {
            const cur = cc.currency || 'SGD';
            if (!ccAgg[cur]) ccAgg[cur] = { currency: cur, count: 0, totalNative: 0 };
            ccAgg[cur].count += 1;
            ccAgg[cur].totalNative = fmt2(ccAgg[cur].totalNative + (parseFloat(cc[`balance${cur}`]) || 0));
        });

        const sanitizeBudgetCategory = cat => cat ? cat.replace(/\*\d+/g, '').trim() : cat;
        const sanitizeBudget = (items, sourceCur = 'SGD') => (items || []).map(item => ({
            category: sanitizeBudgetCategory(item.category),
            amountOriginal: fmt2(item.amount),
            currency: sourceCur,
            amountBase: fmt2(toBaseLocal(item.amount, sourceCur)),
            enabled: item.enabled,
            actualSpentOriginal: fmt2(item.actualSpent || 0),
            actualSpentBase: fmt2(toBaseLocal(item.actualSpent || 0, sourceCur)),
        }));

        const totalGoldValueBase = fmt2(Object.values(goldByType).reduce((s, g) => s + g.totalValueBase, 0));
        const totalPersonalLoanRemainingBase = fmt2(sanitizedPersonalLoans.reduce((s, l) => s + toBaseLocal(l.principalRemaining, l.currency || 'SGD'), 0));
        const totalPersonalLoanMonthlyBase = fmt2(sanitizedPersonalLoans.reduce((s, l) => s + toBaseLocal(l.monthlyInstallment || 0, l.currency || 'SGD'), 0));
        const totalGoldLoanOwedBase = fmt2(sanitizedGoldLoans.reduce((s, l) => s + toBaseLocal((l.principalRemaining || 0) + (l.unpaidInterest || 0), l.currency || 'LKR'), 0));
        const totalCashBase = fmt2(Object.values(accountAgg).reduce((s, a) => s + toBaseLocal(a.totalNative, a.currency), 0));
        const totalCCBase = fmt2(Object.values(ccAgg).reduce((s, c) => s + toBaseLocal(c.totalNative, c.currency), 0));
        const netWorthBase = fmt2(totalGoldValueBase + totalCashBase - totalPersonalLoanRemainingBase - totalGoldLoanOwedBase - totalCCBase);

        const enrichedPersonalLoans = sanitizedPersonalLoans.map(l => ({
            ...l, baseCurrency: _base,
            principalRemainingBase: fmt2(toBaseLocal(l.principalRemaining, l.currency || 'SGD')),
            monthlyInstallmentBase: fmt2(toBaseLocal(l.monthlyInstallment || 0, l.currency || 'SGD')),
        }));
        const enrichedGoldLoans = sanitizedGoldLoans.map(l => ({
            ...l, baseCurrency: _base,
            totalOwedBase: fmt2(toBaseLocal((l.principalRemaining || 0) + (l.unpaidInterest || 0), l.currency || 'LKR')),
            monthlyInterestBase: fmt2(toBaseLocal(l.monthlyInterestAmount || 0, l.currency || 'LKR')),
        }));

        return {
            reportType: 'iPlanner-FI-Report',
            reportDate: new Date().toISOString(),
            baseCurrency: _base,
            disclaimer: `All amounts in the summary and totals are expressed in ${_base}. Raw amounts in original currencies are also included per item for reference. Personally identifiable information has been removed.`,
            exchangeRates: { baseCurrency: _base, lkrPerSgd, sgdPerUsd, goldPriceSgdPerGram22K: goldPrice },
            summary: {
                baseCurrency: _base,
                totalGoldValue: totalGoldValueBase,
                totalPersonalLoanRemaining: totalPersonalLoanRemainingBase,
                totalPersonalLoanMonthlyPayment: totalPersonalLoanMonthlyBase,
                totalGoldLoanOwed: totalGoldLoanOwedBase,
                totalCashAndBankBalance: totalCashBase,
                totalCreditCardOutstanding: totalCCBase,
                netWorthEstimate: netWorthBase,
                note: `All values in the summary are expressed in ${_base} (base currency). Individual items include original currency values for reference.`,
            },
            goldInventory: {
                description: `Gold aggregated by jewellery type and karat. Values shown in market rate converted to ${_base}.`,
                byTypeAndQuality: Object.values(goldByType),
            },
            personalLoans: {
                description: `Personal/home loans. Amounts shown in original currency and ${_base}.`,
                baseCurrency: _base, loans: enrichedPersonalLoans,
                totalMonthlyInstallmentBase: totalPersonalLoanMonthlyBase,
                totalRemainingBase: totalPersonalLoanRemainingBase,
            },
            goldLoans: {
                description: `Gold-backed loans. Amounts shown in original currency and ${_base}.`,
                baseCurrency: _base, loans: enrichedGoldLoans,
                totalOwedBase: totalGoldLoanOwedBase,
            },
            bankAccounts: {
                description: `Bank balances aggregated by country and currency. Total expressed in ${_base}.`,
                baseCurrency: _base, totalBase: totalCashBase,
                aggregatedByRegionCurrency: Object.values(accountAgg).map(a => ({
                    country: a.country, currency: a.currency, accountCount: a.accountCount,
                    totalNative: a.totalNative, totalBase: fmt2(toBaseLocal(a.totalNative, a.currency)), baseCurrency: _base,
                })),
            },
            creditCards: {
                description: `Credit card outstanding balances. Total expressed in ${_base}.`,
                baseCurrency: _base, totalBase: totalCCBase, byCurrency: Object.values(ccAgg),
            },
            budgets: {
                description: 'Monthly budgets with amounts converted to base currency.',
                sgCycle1: sanitizeBudget(data.sgBudgetCycle1, 'SGD'),
                sgCycle2: sanitizeBudget(data.sgBudgetCycle2, 'SGD'),
                lkDamith: sanitizeBudget(data.lkBudgetDamith, 'LKR'),
                lkShan: sanitizeBudget(data.lkBudgetShan, 'LKR'),
            },
        };
    }, [getAllData]);

    // ── Advisor Report: enriched sanitized export for financial advisors ──
    // Extends generateFIReport() with narrative summary, health indicators,
    // investment breakdown, and scenario placeholders. Fully PII-stripped.
    const generateAdvisorReport = useCallback(() => {
        const base = generateFIReport();
        const data = getAllData();
        const fmt2 = n => Math.round((n || 0) * 100) / 100;
        const _base = base.baseCurrency;
        const lkrPerSgd = base.exchangeRates.lkrPerSgd;
        const sgdPerUsd = base.exchangeRates.sgdPerUsd;

        const toBaseLocal = (amount, fromCur) => {
            const n = parseFloat(amount) || 0;
            let inSGD = fromCur === 'LKR' ? n / lkrPerSgd : fromCur === 'USD' ? n * sgdPerUsd : n;
            if (_base === 'LKR') return inSGD * lkrPerSgd;
            if (_base === 'USD') return inSGD / sgdPerUsd;
            return inSGD;
        };

        const totalAssets = fmt2(
            base.summary.totalGoldValue +
            base.summary.totalCashAndBankBalance +
            (Array.isArray(data.investments) ? data.investments.reduce((s, i) => s + toBaseLocal((i.units || 0) * (i.currentPriceUsd || 0), 'USD'), 0) : 0)
        );
        const totalLiabilities = fmt2(base.summary.totalPersonalLoanRemaining + base.summary.totalGoldLoanOwed + base.summary.totalCreditCardOutstanding);
        const debtToAssetRatio = totalAssets > 0 ? fmt2(totalLiabilities / totalAssets) : null;
        const totalMonthlyDebtService = fmt2(
            base.summary.totalPersonalLoanMonthlyPayment +
            (base.goldLoans?.loans || []).reduce((s, l) => s + (l.monthlyInterestBase || 0), 0)
        );

        const totalMonthlyIncomeBase = fmt2((data.monthlyIncomes || []).reduce((s, inc) => {
            const amt = parseFloat(inc.amount) || 0;
            const cur = inc.currency || 'SGD';
            const freq = inc.frequency || 'monthly';
            const monthly = freq === 'annual' ? amt / 12 : freq === 'weekly' ? amt * 4.33 : amt;
            return s + toBaseLocal(monthly, cur);
        }, 0));
        const debtServiceCoverage = totalMonthlyIncomeBase > 0 ? fmt2(totalMonthlyIncomeBase / totalMonthlyDebtService) : null;

        const snapshots = Array.isArray(data.monthlySnapshots) ? data.monthlySnapshots : [];
        const scoreTrend = [...snapshots]
            .sort((a, b) => b.period.localeCompare(a.period))
            .slice(0, 6)
            .map(s => ({ period: s.period, budgetScore: s.budgetScore, loanScore: s.loanScore, overallScore: s.overallScore }))
            .reverse();

        const investmentsByType = {};
        (data.investments || []).forEach(inv => {
            const type = inv.type || 'other';
            if (!investmentsByType[type]) investmentsByType[type] = { type, count: 0, marketValueBase: 0, costBase: 0, unrealisedPnlBase: 0 };
            const mktUSD = (inv.units || 0) * (inv.currentPriceUsd || 0);
            const costUSD = (inv.units || 0) * (inv.avgCostUsd || 0);
            investmentsByType[type].count += 1;
            investmentsByType[type].marketValueBase = fmt2(investmentsByType[type].marketValueBase + toBaseLocal(mktUSD, 'USD'));
            investmentsByType[type].costBase = fmt2(investmentsByType[type].costBase + toBaseLocal(costUSD, 'USD'));
            investmentsByType[type].unrealisedPnlBase = fmt2(investmentsByType[type].marketValueBase - investmentsByType[type].costBase);
        });
        const cashWallets = { SGD: data.invCashSGD || 0, USD: data.invCashUSD || 0 };
        const mmfTotal = fmt2((data.moneyMarketFunds || []).reduce((s, f) => s + toBaseLocal(f.totalValue || 0, f.currency || 'SGD'), 0));

        const nw = base.summary.netWorthEstimate;
        const sym = _base === 'LKR' ? '₨' : _base === 'USD' ? '$' : 'S$';
        const fmtN = n => sym + Math.abs(Math.round(n || 0)).toLocaleString();
        const latestScore = scoreTrend.length > 0 ? scoreTrend[scoreTrend.length - 1] : null;
        const scoreDesc = latestScore
            ? (latestScore.overallScore >= 80 ? 'strong' : latestScore.overallScore >= 60 ? 'moderate' : 'below-target')
            : 'unscored';
        const dtrDesc = debtToAssetRatio !== null
            ? (debtToAssetRatio < 0.3 ? 'healthy (below 30%)' : debtToAssetRatio < 0.6 ? 'moderate (30–60%)' : 'elevated (above 60%)')
            : 'unavailable';
        const narrativeSummary = [
            `Estimated net worth stands at ${fmtN(nw)} (${_base}) as of ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}.`,
            `Total assets are approximately ${fmtN(totalAssets)} against total liabilities of ${fmtN(totalLiabilities)}, yielding a debt-to-asset ratio that is ${dtrDesc}.`,
            `Monthly debt service obligations total ${fmtN(totalMonthlyDebtService)}${totalMonthlyIncomeBase > 0 ? `, representing a debt-service-coverage ratio of ${debtServiceCoverage}×` : ''}.`,
            latestScore ? `The most recent financial discipline score is ${latestScore.overallScore}/100 (${scoreDesc}), comprising a budget score of ${latestScore.budgetScore ?? 'N/A'} and a loan repayment score of ${latestScore.loanScore ?? 'N/A'}.` : '',
        ].filter(Boolean).join(' ');

        return {
            ...base,
            reportType: 'iPlanner-Advisor-Report',
            narrativeSummary,
            healthIndicators: {
                totalAssetsBase: totalAssets, totalLiabilitiesBase: totalLiabilities,
                debtToAssetRatio, totalMonthlyDebtServiceBase: totalMonthlyDebtService,
                totalMonthlyIncomeBase, debtServiceCoverageRatio: debtServiceCoverage,
                baseCurrency: _base,
            },
            scoreTrend,
            investments: {
                description: 'Securities portfolio aggregated by type. All values in base currency.',
                baseCurrency: _base, byType: Object.values(investmentsByType),
                cashWallets, moneyMarketFundsBase: mmfTotal,
            },
            scenarioPlaceholders: {
                earlySettlementImpact: '[To be completed by advisor — effect of early loan settlement on cash flow]',
                additionalInvestmentCapacity: '[To be completed by advisor — estimated monthly surplus available for investment]',
                riskProfile: '[To be completed by advisor — conservative / balanced / growth]',
                recommendedActions: '[To be completed by advisor]',
            },
        };
    }, [generateFIReport, getAllData]);

    // ── generateReportHTML: shared HTML renderer for executive reports ──
    // Returns a self-contained HTML string (inline styles, no external deps).
    // type: 'advisor' | 'household'
    const generateReportHTML = useCallback((type, reportData) => {
        const sym = reportData.baseCurrency === 'LKR' ? '₨' : reportData.baseCurrency === 'USD' ? '$' : 'S$';
        const fmtN = (n, decimals = 0) => sym + (Math.abs(n || 0)).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
        const fmtPct = n => (n == null ? 'N/A' : Math.round(n * 100) + '%');
        const periodLabel = new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        const generatedAt = new Date().toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
        const titleMap = { advisor: 'Financial Advisor Report', household: 'Household Financial Summary' };
        const subtitleMap = { advisor: 'Confidential — For Advisor Use Only', household: 'Family Financial Overview' };
        const accentColor = type === 'advisor' ? '#8B5CF6' : '#10B981';
        const title = titleMap[type] || 'Financial Report';
        const subtitle = subtitleMap[type] || '';

        const s = reportData.summary || {};
        const rates = reportData.exchangeRates || {};

        const trendRows = (reportData.scoreTrend || []).map(t =>
            `<tr><td>${t.period}</td><td>${t.budgetScore ?? '—'}</td><td>${t.loanScore ?? '—'}</td><td><strong>${t.overallScore ?? '—'}</strong></td></tr>`
        ).join('');

        const personalLoanRows = ((reportData.personalLoans || {}).loans || []).map(l =>
            `<tr><td>${l.loanRef || l.name || '—'}</td><td>${l.type || '—'}</td><td>${l.currency} ${(l.principalRemaining || 0).toLocaleString()}</td><td>${l.monthsLeft ?? '—'} mo</td><td>${l.currency} ${(l.monthlyInstallment || 0).toLocaleString()}</td><td>${l.settlementDate || '—'}</td></tr>`
        ).join('');
        const goldLoanRows = ((reportData.goldLoans || {}).loans || []).map(l =>
            `<tr><td>${l.loanRef || l.name || '—'}</td><td>${(l.goldWeightG || 0).toFixed(1)}g gold</td><td>${l.currency} ${(l.principalRemaining || 0).toLocaleString()}</td><td>${l.currency} ${(l.unpaidInterest || 0).toLocaleString()}</td><td>${l.currency} ${(l.monthlyInterestAmount || 0).toLocaleString()}</td><td>—</td></tr>`
        ).join('');

        const invRows = type === 'advisor' ? ((reportData.investments || {}).byType || []).map(t =>
            `<tr><td>${t.type}</td><td>${t.count}</td><td>${fmtN(t.marketValueBase)}</td><td>${fmtN(t.costBase)}</td><td style="color:${t.unrealisedPnlBase >= 0 ? '#10B981' : '#EF4444'}">${fmtN(t.unrealisedPnlBase)}</td></tr>`
        ).join('') : '';

        const renderBudgetSection = (label, items) => {
            if (!items || items.length === 0) return '';
            const rows = items.map(i => {
                const pct = i.amountBase > 0 ? Math.round((i.actualSpentBase / i.amountBase) * 100) : 0;
                const color = pct > 100 ? '#EF4444' : pct >= 90 ? '#F59E0B' : '#10B981';
                return `<tr><td>${i.category}</td><td>${fmtN(i.amountBase)}</td><td>${fmtN(i.actualSpentBase)}</td><td style="color:${color};font-weight:600">${pct}%</td></tr>`;
            }).join('');
            return `<h4 style="margin:12px 0 6px;color:#94A3B8;font-size:12px;text-transform:uppercase;letter-spacing:0.5px">${label}</h4>
            <table><thead><tr><th>Category</th><th>Budgeted</th><th>Spent</th><th>Used</th></tr></thead><tbody>${rows}</tbody></table>`;
        };

        const budgets = reportData.budgets || {};
        const hi = reportData.healthIndicators || {};

        const healthSection = type === 'advisor' ? `
        <div class="section">
            <h2>Financial Health Indicators</h2>
            <div class="kpi-grid">
                <div class="kpi"><div class="kpi-label">Total Assets</div><div class="kpi-value">${fmtN(hi.totalAssetsBase)}</div></div>
                <div class="kpi"><div class="kpi-label">Total Liabilities</div><div class="kpi-value">${fmtN(hi.totalLiabilitiesBase)}</div></div>
                <div class="kpi"><div class="kpi-label">Debt-to-Asset Ratio</div><div class="kpi-value">${hi.debtToAssetRatio != null ? fmtPct(hi.debtToAssetRatio) : 'N/A'}</div></div>
                <div class="kpi"><div class="kpi-label">Monthly Debt Service</div><div class="kpi-value">${fmtN(hi.totalMonthlyDebtServiceBase)}</div></div>
                <div class="kpi"><div class="kpi-label">Monthly Income</div><div class="kpi-value">${hi.totalMonthlyIncomeBase > 0 ? fmtN(hi.totalMonthlyIncomeBase) : 'Not recorded'}</div></div>
                <div class="kpi"><div class="kpi-label">Debt Service Coverage</div><div class="kpi-value">${hi.debtServiceCoverageRatio != null ? hi.debtServiceCoverageRatio + '×' : 'N/A'}</div></div>
            </div>
        </div>` : '';

        const invSection = type === 'advisor' && invRows ? `
        <div class="section">
            <h2>Investment Portfolio</h2>
            <table><thead><tr><th>Type</th><th>Count</th><th>Market Value</th><th>Cost Basis</th><th>Unrealised P&amp;L</th></tr></thead><tbody>${invRows}</tbody></table>
            <p style="margin-top:8px;font-size:12px;color:#64748B">Cash wallets: SGD ${(reportData.investments?.cashWallets?.SGD || 0).toLocaleString()} · USD ${(reportData.investments?.cashWallets?.USD || 0).toLocaleString()} · MMF ${fmtN(reportData.investments?.moneyMarketFundsBase || 0)}</p>
        </div>` : '';

        const scenarioSection = type === 'advisor' && reportData.scenarioPlaceholders ? `
        <div class="section">
            <h2>Scenarios &amp; Advisor Notes</h2>
            <table><thead><tr><th>Area</th><th>Advisor Input</th></tr></thead><tbody>
                <tr><td>Early Settlement Impact</td><td style="color:#64748B;font-style:italic">${reportData.scenarioPlaceholders.earlySettlementImpact}</td></tr>
                <tr><td>Additional Investment Capacity</td><td style="color:#64748B;font-style:italic">${reportData.scenarioPlaceholders.additionalInvestmentCapacity}</td></tr>
                <tr><td>Risk Profile</td><td style="color:#64748B;font-style:italic">${reportData.scenarioPlaceholders.riskProfile}</td></tr>
                <tr><td>Recommended Actions</td><td style="color:#64748B;font-style:italic">${reportData.scenarioPlaceholders.recommendedActions}</td></tr>
            </tbody></table>
        </div>` : '';

        return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title} — ${periodLabel}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#F8FAFC;color:#1E293B;font-size:14px;line-height:1.6}
.page{max-width:900px;margin:0 auto;padding:40px 32px}
.cover{background:linear-gradient(135deg,#0F172A 0%,#1E293B 100%);color:#F8FAFC;padding:40px;border-radius:12px;margin-bottom:32px}
.cover h1{font-size:28px;font-weight:800;margin-bottom:6px}
.cover .subtitle{font-size:14px;opacity:0.6;margin-bottom:20px}
.cover .meta{display:flex;gap:24px;flex-wrap:wrap}
.cover .meta-item{font-size:12px;opacity:0.7}
.cover .meta-item strong{font-size:15px;color:${accentColor};display:block}
.accent{color:${accentColor}}
.section{background:#fff;border:1px solid #E2E8F0;border-radius:10px;padding:24px;margin-bottom:20px}
.section h2{font-size:16px;font-weight:700;color:#0F172A;margin-bottom:16px;padding-bottom:8px;border-bottom:2px solid ${accentColor}}
.section h4{margin:12px 0 6px;color:#94A3B8;font-size:12px;text-transform:uppercase;letter-spacing:.5px}
.kpi-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:12px}
.kpi{background:#F8FAFC;border:1px solid #E2E8F0;border-radius:8px;padding:14px}
.kpi-label{font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
.kpi-value{font-size:18px;font-weight:700;color:#0F172A}
table{width:100%;border-collapse:collapse;font-size:13px}
th{background:#F1F5F9;padding:8px 10px;text-align:left;font-weight:600;color:#475569;font-size:11px;text-transform:uppercase;letter-spacing:.4px}
td{padding:8px 10px;border-bottom:1px solid #F1F5F9;color:#334155}
tr:last-child td{border-bottom:none}
.narrative{background:#F8FAFC;border-left:4px solid ${accentColor};padding:16px;border-radius:0 8px 8px 0;font-size:14px;line-height:1.7;color:#334155;margin-bottom:16px}
.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600}
.badge-green{background:#D1FAE5;color:#065F46}
.badge-amber{background:#FEF3C7;color:#92400E}
.badge-red{background:#FEE2E2;color:#991B1B}
.footer{text-align:center;font-size:11px;color:#94A3B8;margin-top:32px;padding-top:16px;border-top:1px solid #E2E8F0}
.back-bar{position:sticky;top:0;left:0;right:0;z-index:100;background:#0F172A;padding:12px 16px;display:flex;align-items:center;gap:12px;border-bottom:1px solid rgba(255,255,255,0.1)}
.back-btn{background:rgba(255,215,0,0.1);border:1px solid rgba(255,215,0,0.3);color:#FFD700;padding:8px 14px;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;text-decoration:none}
.back-btn:hover{background:rgba(255,215,0,0.15)}
.back-title{color:#94A3B8;font-size:13px;font-weight:500}
@media print{.back-bar{display:none}body{background:#fff}.section{break-inside:avoid}}
</style>
</head>
<body>
<div class="back-bar">
<button class="back-btn" onclick="window.close() || history.back()">
<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
Back to App
</button>
<span class="back-title">${title}</span>
</div>
<div class="page">
<div class="cover">
<h1>${title}</h1>
<div class="subtitle">${subtitle}</div>
<div class="meta">
<div class="meta-item"><strong>${periodLabel}</strong>Report Period</div>
<div class="meta-item"><strong>${generatedAt}</strong>Generated</div>
<div class="meta-item"><strong>${reportData.baseCurrency}</strong>Base Currency</div>
<div class="meta-item"><strong>1 SGD = ${rates.lkrPerSgd || '—'} LKR</strong>Exchange Rate</div>
</div>
</div>

${reportData.narrativeSummary ? `<div class="section"><h2>Executive Summary</h2><div class="narrative">${reportData.narrativeSummary}</div></div>` : ''}

<div class="section">
<h2>Net Worth Snapshot</h2>
<div class="kpi-grid">
<div class="kpi"><div class="kpi-label">Net Worth</div><div class="kpi-value accent">${fmtN(s.netWorthEstimate)}</div></div>
<div class="kpi"><div class="kpi-label">Cash &amp; Bank</div><div class="kpi-value">${fmtN(s.totalCashAndBankBalance)}</div></div>
<div class="kpi"><div class="kpi-label">Gold Value</div><div class="kpi-value">${fmtN(s.totalGoldValue)}</div></div>
${s.totalInvestmentsBase ? `<div class="kpi"><div class="kpi-label">Investments</div><div class="kpi-value">${fmtN(s.totalInvestmentsBase)}</div></div>` : ''}
${s.totalFixedAssetsBase ? `<div class="kpi"><div class="kpi-label">Fixed Assets</div><div class="kpi-value">${fmtN(s.totalFixedAssetsBase)}</div></div>` : ''}
<div class="kpi"><div class="kpi-label">Total Loans</div><div class="kpi-value">${fmtN((s.totalPersonalLoanRemaining || 0) + (s.totalGoldLoanOwed || 0))}</div></div>
<div class="kpi"><div class="kpi-label">Credit Cards</div><div class="kpi-value">${fmtN(s.totalCreditCardOutstanding)}</div></div>
<div class="kpi"><div class="kpi-label">Monthly Loan Payments</div><div class="kpi-value">${fmtN(s.totalPersonalLoanMonthlyPayment)}</div></div>
</div>
</div>

${type === 'household' && reportData.goldInventory?.byTypeAndQuality?.length ? `
<div class="section">
<h2>Gold Inventory</h2>
<table><thead><tr><th>Type</th><th>Karat</th><th>Count</th><th>Weight (g)</th><th>Pawn Value</th><th>Market Value</th></tr></thead><tbody>
${(reportData.goldInventory.byTypeAndQuality).map(g => `<tr><td>${g.type}</td><td>${g.quality || '22K'}</td><td>${g.count}</td><td>${(g.totalWeightG || 0).toFixed(1)}</td><td>${fmtN(g.totalPawn)}</td><td>${fmtN(g.totalValueBase)}</td></tr>`).join('')}
</tbody></table>
</div>` : ''}

${type === 'household' && reportData.bankAccounts?.aggregatedByRegionCurrency?.length ? `
<div class="section">
<h2>Bank Balances</h2>
<table><thead><tr><th>Country</th><th>Currency</th><th>Accounts</th><th>Total (native)</th><th>Total (${reportData.baseCurrency})</th></tr></thead><tbody>
${(reportData.bankAccounts.aggregatedByRegionCurrency).map(a => `<tr><td>${a.country || '—'}</td><td>${a.currency}</td><td>${a.accountCount}</td><td>${a.currency} ${(a.totalNative || 0).toLocaleString()}</td><td>${fmtN(a.totalBase)}</td></tr>`).join('')}
</tbody></table>
</div>` : ''}

${healthSection}

<div class="section">
<h2>Loan Schedule</h2>
${personalLoanRows ? `<h4>Personal &amp; Home Loans</h4><table><thead><tr><th>Ref</th><th>Type</th><th>Remaining</th><th>Months Left</th><th>Monthly</th><th>Settlement</th></tr></thead><tbody>${personalLoanRows}</tbody></table>` : '<p style="color:#64748B;font-size:13px">No personal loans recorded.</p>'}
${goldLoanRows ? `<h4 style="margin-top:16px">Gold-Backed Loans</h4><table><thead><tr><th>Ref</th><th>Collateral</th><th>Principal</th><th>Unpaid Interest</th><th>Monthly Interest</th><th>Notes</th></tr></thead><tbody>${goldLoanRows}</tbody></table>` : ''}
</div>

${invSection}

<div class="section">
<h2>Budget Performance</h2>
${renderBudgetSection('SG Cycle 1', budgets.sgCycle1)}
${renderBudgetSection('SG Cycle 2', budgets.sgCycle2)}
${renderBudgetSection('LK Budget A', budgets.lkDamith)}
${renderBudgetSection('LK Budget B', budgets.lkShan)}
</div>

${trendRows ? `<div class="section"><h2>Discipline Score Trend</h2><table><thead><tr><th>Period</th><th>Budget Score</th><th>Loan Score</th><th>Overall</th></tr></thead><tbody>${trendRows}</tbody></table></div>` : ''}

${scenarioSection}

${reportData.actionItems?.length ? `
<div class="section">
<h2>Action Items</h2>
<table><thead><tr><th>Priority</th><th>Item</th></tr></thead><tbody>
${reportData.actionItems.map(a => `<tr><td><span class="badge ${a.type === 'alert' ? 'badge-red' : 'badge-amber'}">${a.type === 'alert' ? 'Alert' : 'Reminder'}</span></td><td>${a.text}</td></tr>`).join('')}
</tbody></table>
</div>` : ''}

<div class="footer">
<p>${reportData.disclaimer || ''}</p>
<p style="margin-top:4px">Generated by iPlanner · ${generatedAt}</p>
</div>
</div>
</body>
</html>`;
    }, [generateFIReport, getAllData]);

    // ── Household Report: HTML export ─────────────────────────
    const generateHouseholdReport = useCallback(() => {
        const data = getAllData();
        const fmt2 = n => Math.round((n || 0) * 100) / 100;
        const _base = data.baseCurrency || 'SGD';
        const lkrPerSgd = parseFloat(data.lkrRate) || 245;
        const sgdPerUsd = parseFloat(data.usdRate) || 1.26;
        const goldPrice = parseFloat(data.sgdRate) || 186;

        const toBaseLocal = (amount, fromCur) => {
            const n = parseFloat(amount) || 0;
            let inSGD = fromCur === 'LKR' ? n / lkrPerSgd : fromCur === 'USD' ? n * sgdPerUsd : n;
            if (_base === 'LKR') return inSGD * lkrPerSgd;
            if (_base === 'USD') return inSGD / sgdPerUsd;
            return inSGD;
        };

        const goldByType = {};
        (data.goldInventory || []).forEach(item => {
            const key = `${item.type} (${item.quality || '22K'})`;
            if (!goldByType[key]) goldByType[key] = { type: item.type, quality: item.quality, count: 0, totalWeightG: 0, totalPawn: 0, totalValueBase: 0 };
            const weight = parseFloat(item.weight) || 0;
            const qualityFactor = item.quality === '18K' ? 0.75 : 1.0;
            goldByType[key].count += 1;
            goldByType[key].totalWeightG = fmt2(goldByType[key].totalWeightG + weight);
            goldByType[key].totalPawn = fmt2(goldByType[key].totalPawn + (item.pawn || 0));
            goldByType[key].totalValueBase = fmt2(goldByType[key].totalValueBase + toBaseLocal(weight * goldPrice * qualityFactor, 'SGD'));
        });

        const sanitizedPersonalLoans = (data.personalLoans || []).map((loan, idx) => ({
            loanRef: loan.name || `Loan ${idx + 1}`,
            name: (loan.name || '').replace(/\*\d+/g, '').trim(),
            type: loan.type || 'Personal Loan',
            currency: loan.currency || 'SGD',
            principalRemaining: fmt2(loan.principalRemaining),
            monthsLeft: loan.monthsLeft,
            monthlyInstallment: fmt2(loan.monthlyInstallment),
            settlementDate: loan.settlementDate || '',
            principalRemainingBase: fmt2(toBaseLocal(loan.principalRemaining, loan.currency || 'SGD')),
            monthlyInstallmentBase: fmt2(toBaseLocal(loan.monthlyInstallment || 0, loan.currency || 'SGD')),
        }));
        const sanitizedGoldLoans = (data.goldLoans || []).map((loan, idx) => ({
            name: loan.name || `Gold Loan ${idx + 1}`,
            currency: loan.currency || 'LKR',
            goldWeightG: fmt2(loan.goldWeight),
            principalRemaining: fmt2(loan.principalRemaining),
            unpaidInterest: fmt2(loan.unpaidInterest),
            monthlyInterest: fmt2(loan.monthlyInterest),
            totalOwedBase: fmt2(toBaseLocal((loan.principalRemaining || 0) + (loan.unpaidInterest || 0), loan.currency || 'LKR')),
        }));

        const accountAgg = {};
        (data.accounts || []).forEach(acc => {
            const key = `${acc.country}-${acc.currency}`;
            if (!accountAgg[key]) accountAgg[key] = { country: acc.country, currency: acc.currency, accountCount: 0, totalNative: 0 };
            accountAgg[key].accountCount += 1;
            accountAgg[key].totalNative = fmt2(accountAgg[key].totalNative + (parseFloat(acc[`balance${acc.currency}`]) || 0));
        });

        const ccAgg = {};
        (data.creditCards || []).forEach(cc => {
            const cur = cc.currency || 'SGD';
            if (!ccAgg[cur]) ccAgg[cur] = { currency: cur, count: 0, totalNative: 0 };
            ccAgg[cur].count += 1;
            ccAgg[cur].totalNative = fmt2(ccAgg[cur].totalNative + (parseFloat(cc[`balance${cur}`]) || 0));
        });

        const totalGoldValueBase = fmt2(Object.values(goldByType).reduce((s, g) => s + g.totalValueBase, 0));
        const totalCashBase = fmt2(Object.values(accountAgg).reduce((s, a) => s + toBaseLocal(a.totalNative, a.currency), 0));
        const totalInvBase = fmt2(
            (data.investments || []).reduce((s, i) => s + toBaseLocal((i.units || 0) * (i.currentPriceUsd || 0), 'USD'), 0) +
            toBaseLocal(data.invCashSGD || 0, 'SGD') + toBaseLocal(data.invCashUSD || 0, 'USD') +
            (data.moneyMarketFunds || []).reduce((s, f) => s + toBaseLocal(f.totalValue || 0, f.currency || 'SGD'), 0)
        );
        const fixedAggLKR = (data.fixedAssets || []).reduce((s, fa) => s + (fa.cmvLKR || fa.valuationLKR || 0), 0);
        const totalFixedBase = fmt2(toBaseLocal(fixedAggLKR, 'LKR'));
        const totalPersonalLoanRemainingBase = fmt2(sanitizedPersonalLoans.reduce((s, l) => s + l.principalRemainingBase, 0));
        const totalGoldLoanOwedBase = fmt2(sanitizedGoldLoans.reduce((s, l) => s + l.totalOwedBase, 0));
        const totalCCBase = fmt2(Object.values(ccAgg).reduce((s, c) => s + toBaseLocal(c.totalNative, c.currency), 0));
        const totalMonthlyLoanBase = fmt2(
            sanitizedPersonalLoans.reduce((s, l) => s + l.monthlyInstallmentBase, 0) +
            sanitizedGoldLoans.reduce((s, l) => s + toBaseLocal(l.monthlyInterest || 0, l.currency || 'LKR'), 0)
        );
        const netWorthBase = fmt2(totalGoldValueBase + totalCashBase + totalInvBase + totalFixedBase - totalPersonalLoanRemainingBase - totalGoldLoanOwedBase - totalCCBase);

        const snapshots = Array.isArray(data.monthlySnapshots) ? data.monthlySnapshots : [];
        const scoreTrend = [...snapshots]
            .sort((a, b) => b.period.localeCompare(a.period))
            .slice(0, 5)
            .map(s => ({ period: s.period, budgetScore: s.budgetScore, loanScore: s.loanScore, overallScore: s.overallScore }))
            .reverse();

        const sanitizeBudgetCat = cat => (cat || '').replace(/\*\d+/g, '').trim();
        const sanitizeBudget = (items, cur) => (items || []).map(i => ({
            category: sanitizeBudgetCat(i.category),
            amountBase: fmt2(toBaseLocal(i.amount, cur)),
            actualSpentBase: fmt2(toBaseLocal(i.actualSpent || 0, cur)),
            enabled: i.enabled,
        }));

        const actions = [];
        (data.personalLoans || []).forEach(loan => {
            const history = Array.isArray(loan.paymentHistory) ? loan.paymentHistory : [];
            const monthStr = new Date().toISOString().slice(0, 7);
            if (!history.find(e => e.period === monthStr)) {
                actions.push({ type: 'warning', text: `Loan payment not yet recorded for ${(loan.name || '').replace(/\*\d+/g, '').trim() || 'a loan'} this month.` });
            }
        });
        const budgetItems = [...(data.sgBudgetCycle1 || []), ...(data.sgBudgetCycle2 || [])];
        budgetItems.forEach(i => {
            if (i.enabled && i.amount > 0 && (i.actualSpent || 0) > i.amount) {
                actions.push({ type: 'alert', text: `Over budget: ${sanitizeBudgetCat(i.category)} (spent ${Math.round((i.actualSpent / i.amount) * 100)}% of budget).` });
            }
        });

        return {
            reportType: 'iPlanner-Household-Report',
            reportDate: new Date().toISOString(),
            baseCurrency: _base,
            disclaimer: `All values expressed in ${_base}. Account numbers and bank names have been excluded.`,
            exchangeRates: { baseCurrency: _base, lkrPerSgd, sgdPerUsd, goldPriceSgdPerGram22K: goldPrice },
            narrativeSummary: null,
            summary: {
                netWorthEstimate: netWorthBase,
                totalCashAndBankBalance: totalCashBase,
                totalGoldValue: totalGoldValueBase,
                totalInvestmentsBase: totalInvBase,
                totalFixedAssetsBase: totalFixedBase,
                totalPersonalLoanRemaining: totalPersonalLoanRemainingBase,
                totalGoldLoanOwed: totalGoldLoanOwedBase,
                totalCreditCardOutstanding: totalCCBase,
                totalMonthlyLoanPayments: totalMonthlyLoanBase,
                totalPersonalLoanMonthlyPayment: totalMonthlyLoanBase,
            },
            personalLoans: { loans: sanitizedPersonalLoans },
            goldLoans: { loans: sanitizedGoldLoans },
            bankAccounts: {
                aggregatedByRegionCurrency: Object.values(accountAgg).map(a => ({
                    ...a, totalBase: fmt2(toBaseLocal(a.totalNative, a.currency)), baseCurrency: _base,
                })),
            },
            creditCards: { byCurrency: Object.values(ccAgg) },
            goldInventory: { byTypeAndQuality: Object.values(goldByType) },
            investments: { totalBase: totalInvBase, description: 'Total portfolio value in base currency.' },
            fixedAssets: { totalBase: totalFixedBase, description: 'Land and property at current market value in base currency.' },
            budgets: {
                sgCycle1: sanitizeBudget(data.sgBudgetCycle1, 'SGD'),
                sgCycle2: sanitizeBudget(data.sgBudgetCycle2, 'SGD'),
                lkDamith: sanitizeBudget(data.lkBudgetDamith, 'LKR'),
                lkShan: sanitizeBudget(data.lkBudgetShan, 'LKR'),
            },
            scoreTrend,
            actionItems: actions,
        };
    }, [getAllData]);

    // ── FI Report: Drive upload ──────────────────────────────
    const shareWithFI = useCallback(async () => {
        if (!authUser) return;
        setFiReportStatus('uploading');
        try {
            const token = await acquireDriveToken();
            const report = generateFIReport();
            await DriveAPI.writeFIReportFile(token, report);
            setFiReportStatus('done');
            setTimeout(() => setFiReportStatus('idle'), 4000);
        } catch (err) {
            console.error('[iPlanner] FI report upload error:', err);
            setFiReportStatus('error');
            setTimeout(() => setFiReportStatus('idle'), 5000);
        }
    }, [authUser, generateFIReport, acquireDriveToken, DriveAPI]);

    // ── Advisor Report: Drive upload & HTML/PDF export ────────────
    const shareAdvisorReport = useCallback(async () => {
        if (!authUser) return;
        setAdvisorReportStatus('uploading');
        try {
            const token = await acquireDriveToken();
            const report = generateAdvisorReport();
            await DriveAPI.writeAdvisorReportFile(token, report);
            setAdvisorReportStatus('done');
            setTimeout(() => setAdvisorReportStatus('idle'), 4000);
        } catch (err) {
            console.error('[iPlanner] Advisor report upload error:', err);
            setAdvisorReportStatus('error');
            setTimeout(() => setAdvisorReportStatus('idle'), 5000);
        }
    }, [authUser, generateAdvisorReport, acquireDriveToken, DriveAPI]);

    const exportAdvisorReportHTML = useCallback(() => {
        const report = generateAdvisorReport();
        const html = generateReportHTML('advisor', report);
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); }
    }, [generateAdvisorReport, generateReportHTML]);

    const exportAdvisorReportPDF = useCallback(() => {
        const report = generateAdvisorReport();
        const html = generateReportHTML('advisor', report);
        const element = document.createElement('div');
        element.innerHTML = html;
        document.body.appendChild(element);
        const opt = {
            margin: 10,
            filename: `iplanner-advisor-report-${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        };
        html2pdf().set(opt).from(element).save().then(() => {
            document.body.removeChild(element);
        });
    }, [generateAdvisorReport, generateReportHTML]);

    // ── Household Report: HTML/PDF export ────────────────────
    const exportHouseholdReportHTML = useCallback(() => {
        const report = generateHouseholdReport();
        const html = generateReportHTML('household', report);
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); }
    }, [generateHouseholdReport, generateReportHTML]);

    const exportHouseholdReportPDF = useCallback(() => {
        const report = generateHouseholdReport();
        const html = generateReportHTML('household', report);
        const element = document.createElement('div');
        element.innerHTML = html;
        document.body.appendChild(element);
        const opt = {
            margin: 10,
            filename: `iplanner-household-report-${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        };
        html2pdf().set(opt).from(element).save().then(() => {
            document.body.removeChild(element);
        });
    }, [generateHouseholdReport, generateReportHTML]);

    return {
        generateFIReport,
        fiReportStatus,
        advisorReportStatus,
        shareWithFI,
        shareAdvisorReport,
        exportAdvisorReportHTML,
        exportAdvisorReportPDF,
        exportHouseholdReportHTML,
        exportHouseholdReportPDF,
    };
}
